/**
 * Circular buffer for console.error calls.
 * Captures the last N console.error messages for inclusion with error reports.
 * PII is scrubbed at write time (push), not read time.
 */

import { scrubPII } from '../pii/index';

const DEFAULT_BUFFER_SIZE = 50;

export class ConsoleBuffer {
  private buffer: string[] = [];
  private maxSize: number;
  private originalConsoleError: ((...args: unknown[]) => void) | null = null;
  private installed = false;

  constructor(maxSize: number = DEFAULT_BUFFER_SIZE) {
    this.maxSize = maxSize;
  }

  /**
   * Install the console.error interceptor.
   * Monkey-patches console.error to capture messages while preserving original behavior.
   */
  install(): void {
    if (this.installed) return;
    if (typeof console === 'undefined') return;

    this.originalConsoleError = console.error.bind(console);

    console.error = (...args: unknown[]) => {
      // Capture the message
      const message = args
        .map(arg => {
          if (typeof arg === 'string') return arg;
          try {
            return JSON.stringify(arg);
          } catch {
            return String(arg);
          }
        })
        .join(' ');

      this.push(message);

      // Call original console.error
      if (this.originalConsoleError) {
        this.originalConsoleError(...args);
      }
    };

    this.installed = true;
  }

  /**
   * Uninstall the console.error interceptor, restoring original behavior.
   */
  uninstall(): void {
    if (!this.installed || !this.originalConsoleError) return;
    console.error = this.originalConsoleError;
    this.originalConsoleError = null;
    this.installed = false;
  }

  /**
   * Push a message into the circular buffer.
   * PII is scrubbed before storage. If the buffer is full, the oldest message is dropped.
   */
  private push(message: string): void {
    const scrubbed = scrubPII(message);
    if (this.buffer.length >= this.maxSize) {
      this.buffer.shift();
    }
    this.buffer.push(scrubbed);
  }

  /**
   * Get a snapshot of the current buffer contents.
   * Returns a copy — modifying the array will not affect the buffer.
   */
  getEntries(): string[] {
    return [...this.buffer];
  }

  /** Clear all buffered entries */
  clear(): void {
    this.buffer = [];
  }
}
